import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * VERDE PARA LA ACCION PRINCIPAL, y el rojo solo en enlaces y en el punto.
 * Es textual del brandbook (lamina 20), y es el unico punto en el que el
 * sistema se aparta de su version anterior, donde la accion primaria era
 * grafito para no competir con los numeros. La contencion se conserva por otra
 * via: hay UNA accion primaria por pantalla y el resto son `outline` o `ghost`.
 *
 * Los estados usan TOKENS y no opacidades: «el hover aclara el verde dos pasos
 * y el presionado baja casi a tinta; el salto entre estados debe verse sin
 * buscarlo». Un `bg-primary/88` no aclara: transparenta, y sobre el folio da un
 * verde sucio en vez del siguiente paso de la escala.
 *
 * El radio es 6px (`--radius`), tambien del brandbook.
 */
const buttonVariants = cva(
  [
    "inline-flex select-none items-center justify-center gap-1.5 whitespace-nowrap",
    "rounded-md font-medium",
    "transition-[background-color,border-color,color,box-shadow] duration-100 ease-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
    "disabled:pointer-events-none disabled:opacity-45",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary-hover active:bg-primary-active",
        destructive:
          "bg-destructive text-destructive-foreground shadow-xs hover:bg-margen-hover active:bg-destructive",
        outline:
          "border border-input bg-surface text-foreground shadow-xs hover:border-border-strong hover:bg-accent",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/70",
        ghost: "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
        link: "text-brand underline-offset-[3px] hover:text-primary-hover hover:underline",
        /*
          EL ENLACE ROJO del brandbook: el unico sitio de la interfaz, junto al
          punto, donde el rojo margen aparece por decision de marca y no por
          alarma. Se reserva para lo que saca de la pantalla —abrir el
          comprobante original, el fundamento— no para una accion destructiva:
          para eso esta `destructive`.
        */
        "link-margen": "text-margen underline-offset-[3px] hover:text-margen-hover hover:underline",
        brand: "bg-brand text-brand-foreground shadow-xs hover:bg-primary-hover active:bg-primary-active",
        success: "bg-success text-success-foreground shadow-xs hover:bg-success/88",
      },
      size: {
        default: "h-8 px-3 text-sm [&_svg]:size-4",
        sm: "h-7 px-2.5 text-xs [&_svg]:size-3.5",
        xs: "h-6 rounded-sm px-1.5 text-2xs [&_svg]:size-3",
        lg: "h-9 px-5 text-base [&_svg]:size-4",
        icon: "h-8 w-8 [&_svg]:size-4",
        iconSm: "h-7 w-7 [&_svg]:size-3.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    )
  },
)
Button.displayName = "Button"

export { Button, buttonVariants }
